import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Typography } from '@material-ui/core';
import { Card, CardActions, CardContent, CardMedia, Switch, FormControlLabel } from '@material-ui/core';
import GridOnOutlinedIcon from '@material-ui/icons/GridOnOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import LayersOutlinedIcon from '@material-ui/icons/LayersOutlined';
import ScheduleOutlinedIcon from '@material-ui/icons/Schedule';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

// custom Typography component
const SummaryDataField = ({ title, value, unit }) => {
  return (
    <Typography variant="body1" color="textSecondary" component="p">
      <b>{title}: </b>
      {value} {unit}
    </Typography>
  );
};

/**
 * @function SummaryRow
 * @description One attribute of the satellite, as the modern card lays them out: an icon, the
 * attribute name, and its value pushed to the right edge. The classic card runs the same data as a
 * sentence ("Resolução ótica: 10 metros"), which is harder to compare across two cards side by
 * side - aligning the values in a column lets the eye run straight down them.
 * @param {Object} props
 * @param {React.ElementType} props.icon - Icon component for this attribute.
 * @param {String} props.title - Attribute name.
 * @param {String|Number} props.value - Attribute value.
 * @param {String} [props.unit] - Unit appended to the value.
 * **Called by:
 * @see {SatelliteCard}
 * @memberof module:Shared
 * @returns {JSX.Element}
 */
const SummaryRow = ({ icon: Icon, title, value, unit }) => {
  const classes = useStyles();

  return (
    <div className={classes.summaryRow}>
      <Icon className={classes.summaryIcon} fontSize="small" />
      <Typography variant="body2" className={classes.summaryTitle}>
        {title}
      </Typography>
      <Typography variant="body2" className={classes.summaryValue}>
        {value}
        {unit ? ` ${unit}` : null}
      </Typography>
    </div>
  );
};
/**
 * @function SatelliteCard
 * @description This component represents a card for a satellite option in the acquisition process.
 * It displays satellite information such as name, image, resolution, activity period, provider, and revisit time.
 * It also includes a button to select the satellite and, if applicable, options to specify satellite collections (e.g., for Landsat).
 * @param {string} props.name - Name of the satellite.
 * @param {string} props.image - URL of the satellite image to display on the card.
 * @param {string} props.resolution - Optical resolution of the satellite.
 * @param {number} props.startYear - The year the satellite started operating.
 * @param {number} props.endYear - The year the satellite stopped operating (or null if still active).
 * @param {string} props.provider - The organization providing the satellite data.
 * @param {number} props.cycle - The revisit time of the satellite in days.
 * @param {Function} props.onChoose - Callback function to handle the selection of the satellite, called with the chosen satellite type and options.
 * @param {boolean} props.enabled - Flag indicating whether the selection button should be enabled or disabled.
 * @param {boolean} props.specifySatellites - Flag indicating whether to show options for specifying satellite (TOA or SR).
 * @param {Array} props.landsatCollectionOptions - Array of available Landsat collection options (e.g., ['LT05', 'LC08']) to display if specifySatellites is true.
 * @memberof module:Shared
 * @returns {JSX.Element} The rendered SatelliteCard component.
 */
const SatelliteCard = ({
  name,
  image,
  resolution,
  startYear,
  endYear,
  provider,
  cycle,
  onChoose,
  enabled,
  specifySatellites = false,
  landsatCollectionOptions = [],
  variant = 'classic',
  description,
}) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const endYearOrNow = endYear || new Date().getFullYear();
  const [landsatColSwitchState, setLandsatColSwitchState] = useState([true, true]);

  // With a single "choose" action the whole card can act as the button; with
  // the SR/TOA dual-button mode a card-wide click would be ambiguous, so it's
  // left out in that case.
  const wholeCardClickable = enabled && !specifySatellites;
  const isModern = variant === 'modern';

  return (
    <Card
      className={[classes.card, isModern && classes.cardModern, wholeCardClickable && classes.cardClickable]
        .filter(Boolean)
        .join(' ')}
      style={{ cursor: wholeCardClickable ? 'pointer' : 'default' }}
      onClick={wholeCardClickable ? () => onChoose('TOA', landsatColSwitchState) : undefined}>
      <CardMedia className={[classes.image, isModern && classes.imageModern].filter(Boolean).join(' ')} image={image} title={name} />
      <CardContent className={isModern ? classes.contentModern : undefined}>
        <Typography gutterBottom variant="h5" component="h2" className={isModern ? classes.nameModern : undefined}>
          {name}
        </Typography>
        {isModern && description && (
          <Typography variant="body2" className={classes.descriptionModern}>
            {description}
          </Typography>
        )}
        <div id={name + '_props'} className={isModern ? classes.summaryList : undefined}>
          {(() => {
            const Field = isModern ? SummaryRow : SummaryDataField;
            return (
              <>
                <Field
                  icon={GridOnOutlinedIcon}
                  title={t('forms.acquisition.1.card.opticalResolution')}
                  value={resolution}
                  unit={t('forms.acquisition.1.card.opticalResolutionUnit')}
                />
                <Field
                  icon={EventOutlinedIcon}
                  title={t('forms.acquisition.1.card.activityPeriod')}
                  value={isModern ? `${startYear} – ${endYearOrNow}` : `${startYear}-${endYearOrNow}`}
                />
                <Field icon={LayersOutlinedIcon} title={t('forms.acquisition.1.card.provider')} value={provider} />
                <Field
                  icon={ScheduleOutlinedIcon}
                  title={t('forms.acquisition.1.card.revisitTime')}
                  value={cycle}
                  unit={t('forms.acquisition.1.card.revisitTimeUnit')}
                />
              </>
            );
          })()}
        </div>
      </CardContent>
      <CardActions className={[classes.actions, isModern && classes.actionsAreaModern].filter(Boolean).join(' ')}>
        <div
          style={
            isModern && landsatCollectionOptions.length === 0
              ? { display: 'none' }
              : { display: 'flex', flexDirection: 'row', paddingBottom: 10, padddingLeft: 10 }
          }>
          {name === 'Landsat' &&
            landsatCollectionOptions.length > 0 &&
            landsatCollectionOptions.map((option, index) => {
              return (
                <FormControlLabel
                  key={`${option}-${index}`}
                  control={
                    <Switch
                      key={option}
                      checked={landsatColSwitchState[index]}
                      onChange={(event, checked) => {
                        const newState = [...landsatColSwitchState];
                        newState[index] = checked;
                        if (!checked) {
                          const otherIndex = index === 0 ? 1 : 0;
                          if (!newState[otherIndex]) newState[otherIndex] = true;
                        }
                        setLandsatColSwitchState(newState);
                      }}
                      className={classes.switch}
                    />
                  }
                  label={option}
                />
              );
            })}
        </div>
        {specifySatellites ? (
          <div style={{ paddingBottom: '16px', display: 'flex', width: '100%', paddingRight: '30px', gap: 10 }}>
            <p style={{ width: '100%' }}>{t('forms.acquisition.1.card.choose')}: </p>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={() => onChoose('SR', landsatColSwitchState)}
              disabled={!enabled}
              style={{ width: '100%' }}>
              SR
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={() => onChoose('TOA', landsatColSwitchState)}
              disabled={!enabled}
              style={{ width: '100%' }}>
              TOA
            </Button>
          </div>
        ) : (
          <div className={isModern ? classes.actionsModern : undefined} style={isModern ? undefined : { display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
            <Button
              className={isModern ? classes.selectButtonModern : classes.mainSelectButton}
              color="primary"
              variant={isModern ? 'contained' : 'text'}
              disableElevation={isModern}
              endIcon={isModern ? <ArrowForwardIcon /> : undefined}
              onClick={(e) => {
                e.stopPropagation();
                onChoose('TOA', landsatColSwitchState);
              }}
              disabled={!enabled}>
              {t('forms.acquisition.1.card.choose')}
            </Button>
          </div>
        )}
      </CardActions>
    </Card>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles .
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  image: {
    height: 200,
    backgroundPosition: 'center',
  },
  /**
   * The modern variant, used by Shoreline. Flat and outlined instead of raised: the accent is
   * carried by the selection button and the hover border, not by a drop shadow, so two cards side
   * by side read as one calm row rather than two floating slabs.
   */
  cardModern: {
    marginBottom: 0,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  imageModern: {
    height: 160,
    margin: theme.spacing(1.5, 1.5, 0),
    borderRadius: 8,
  },
  contentModern: {
    padding: theme.spacing(2),
    flexGrow: 1,
  },
  nameModern: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 4,
  },
  descriptionModern: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  summaryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  summaryIcon: {
    color: theme.palette.text.secondary,
  },
  summaryTitle: {
    color: theme.palette.text.secondary,
    flexGrow: 1,
  },
  summaryValue: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  /**
   * The classic card leaves the action row to its own devices - 8px of MUI padding plus a switch
   * wrapper that pads even when it holds nothing. The modern one lines the button up with the
   * content above it instead, so the card's left and right edges read as one column.
   */
  actionsAreaModern: {
    padding: theme.spacing(0, 2, 2),
  },
  actionsModern: {
    width: '100%',
  },
  selectButtonModern: {
    width: '100%',
    borderRadius: 8,
    padding: theme.spacing(1.25, 2),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: 15,
  },
  description: {
    paddingTop: theme.spacing(1),
  },
  card: {
    marginBottom: theme.spacing(1),
  },
  cardClickable: {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actions: { alignItems: 'flex-start', flexDirection: 'column' },
  switch: {
    '& .MuiSwitch-switchBase': {
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#176819',
        '& + .MuiSwitch-track': {
          backgroundColor: '#65C466',
          opacity: 1,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
      },
    },
  },
}));

export default SatelliteCard;
